import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  formatDateTime,
  type DataTableColumn,
  type StatusBadgeProps,
} from '@berky/ui'
import { useQuery } from '@tanstack/react-query'
import { type FormEvent, useMemo, useState } from 'react'

import {
  type NotificationHistoryItem,
  type NotificationHistoryPage,
  type NotificationJobStatus,
  useNotificationsApi,
} from './api'

const pageSize = 25
const statusLabels: Record<NotificationJobStatus, string> = {
  Queued: 'Wartet',
  Processing: 'Wird verarbeitet',
  RetryScheduled: 'Neuer Versuch geplant',
  Accepted: 'Von Microsoft 365 angenommen',
  Failed: 'Endgültig fehlgeschlagen',
  Suppressed: 'Unterdrückt',
}
const statusTones: Record<NotificationJobStatus, StatusBadgeProps['tone']> = {
  Queued: 'neutral',
  Processing: 'info',
  RetryScheduled: 'warning',
  Accepted: 'success',
  Failed: 'error',
  Suppressed: 'neutral',
}

function isoDate(date: Date) {
  return date.toISOString().slice(0, 10)
}

export function NotificationHistorySection() {
  const api = useNotificationsApi()
  const limits = useMemo(() => {
    const now = new Date()
    const earliest = new Date(now)
    earliest.setDate(earliest.getDate() - 90)
    return { min: isoDate(earliest), max: isoDate(now) }
  }, [])
  const [page, setPage] = useState(1)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [status, setStatus] = useState<'all' | NotificationJobStatus>('all')
  const [originId, setOriginId] = useState('')
  const [typeKey, setTypeKey] = useState('')
  const [recipientDraft, setRecipientDraft] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')

  const history = useQuery({
    queryKey: [
      'notifications',
      'history',
      { page, from, to, status, originId, typeKey, recipientEmail },
    ],
    queryFn: ({ signal }) =>
      api.get<NotificationHistoryPage>('/jobs/history', {
        signal,
        query: {
          page,
          pageSize,
          from: from ? `${from}T00:00:00.000Z` : undefined,
          to: to ? `${to}T23:59:59.999Z` : undefined,
          status: status === 'all' ? undefined : status,
          originId: originId.trim() || undefined,
          typeKey: typeKey.trim() || undefined,
          recipientEmail: recipientEmail || undefined,
        },
      }),
  })

  function applyRecipient(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setPage(1)
    setRecipientEmail(recipientDraft.trim())
  }

  function updateFilter(setter: (value: string) => void, value: string) {
    setPage(1)
    setter(value)
  }

  const columns: DataTableColumn<NotificationHistoryItem>[] = [
    { key: 'updatedAt', header: 'Zeitpunkt', cell: (row) => formatDateTime(row.updatedAt) },
    {
      key: 'origin',
      header: 'Urheber',
      cell: (row) => (row.originKind === 'Platform' ? 'Plattform' : row.originId),
    },
    { key: 'type', header: 'Art', cell: (row) => row.typeKey },
    {
      key: 'recipient',
      header: 'Empfänger',
      cell: (row) => (
        <div>
          <p>{row.recipient}</p>
          <p className="text-xs text-muted-foreground">
            {row.recipientKind === 'ExternalEmail' ? 'extern' : 'Plattformkonto'}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <StatusBadge tone={statusTones[row.status]}>{statusLabels[row.status]}</StatusBadge>
      ),
    },
    { key: 'attempts', header: 'Versuche', cell: (row) => row.attemptCount, align: 'right' },
    { key: 'error', header: 'Letzter Fehler', cell: (row) => row.lastError ?? '—' },
  ]

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Versandprotokoll filtern</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <FormField label="Von" htmlFor="history-from">
            <Input id="history-from" type="date" min={limits.min} max={to || limits.max} value={from} onChange={(event) => updateFilter(setFrom, event.target.value)} />
          </FormField>
          <FormField label="Bis" htmlFor="history-to">
            <Input id="history-to" type="date" min={from || limits.min} max={limits.max} value={to} onChange={(event) => updateFilter(setTo, event.target.value)} />
          </FormField>
          <FormField label="Status" htmlFor="history-status">
            <Select value={status} onValueChange={(value) => { setPage(1); setStatus(value as 'all' | NotificationJobStatus) }}>
              <SelectTrigger id="history-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                {Object.entries(statusLabels).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Modul oder Plattform" htmlFor="history-origin">
            <Input id="history-origin" value={originId} onChange={(event) => updateFilter(setOriginId, event.target.value)} placeholder="z. B. lackierbestand" />
          </FormField>
          <FormField label="Benachrichtigungsart" htmlFor="history-type">
            <Input id="history-type" value={typeKey} onChange={(event) => updateFilter(setTypeKey, event.target.value)} placeholder="Technischer Schlüssel" />
          </FormField>
          <form className="md:col-span-2 xl:col-span-3" onSubmit={applyRecipient}>
            <FormField label="Exakte E-Mail-Adresse" htmlFor="history-recipient" description="Die vollständige Adresse wird nur zur Suche übertragen und nicht im Protokoll angezeigt.">
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input id="history-recipient" type="email" value={recipientDraft} onChange={(event) => setRecipientDraft(event.target.value)} placeholder="name@beispiel.de" />
                <Button type="submit" variant="outline">Suchen</Button>
              </div>
            </FormField>
          </form>
        </CardContent>
      </Card>
      <DataTable
        caption="Versandprotokoll der letzten 90 Tage"
        columns={columns}
        rows={history.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={history.isPending}
        error={history.isError ? { description: 'Das Versandprotokoll konnte nicht geladen werden.', onRetry: () => history.refetch() } : null}
        pagination={{ page: history.data?.page ?? page, pageSize, total: history.data?.total ?? 0 }}
        onPageChange={setPage}
        renderMobileRow={(row) => (
          <div className="space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <p className="font-medium break-words">{row.typeKey}</p>
              <StatusBadge tone={statusTones[row.status]}>{statusLabels[row.status]}</StatusBadge>
            </div>
            <p className="text-sm">{row.recipient}</p>
            <p className="text-sm text-muted-foreground">{row.originKind === 'Platform' ? 'Plattform' : row.originId} · {formatDateTime(row.updatedAt)} · {row.attemptCount} Versuch(e)</p>
            {row.lastError && <p className="text-sm text-destructive-strong break-words">{row.lastError}</p>}
          </div>
        )}
      />
    </div>
  )
}
